/**
 * /forgot-password — Supabase password recovery initiation.
 *
 * Reachable from the login page's "نسيت كلمة المرور؟" link. The user
 * enters their email; we call `supabase.auth.resetPasswordForEmail`
 * which sends them a magic link to `<origin>/reset-password?code=...`.
 *
 * SECURITY POSTURE:
 *   - We ALWAYS show the same "إذا كان البريد مسجلاً، أرسلنا..." success
 *     copy regardless of whether `resetPasswordForEmail` returned an
 *     error. Supabase itself doesn't enumerate users for unknown emails,
 *     but a flaky network response could leak existence to a determined
 *     attacker. Showing identical UX on both branches removes the oracle.
 *   - We never log the email address. We never echo the email in any
 *     thrown error. The plaintext email lives only in the form state
 *     and the outbound `resetPasswordForEmail` payload.
 *   - The `redirectTo` we pass is `window.location.origin + '/reset-password'`.
 *     This URL MUST be added to the Supabase Dashboard under
 *     `Authentication → URL Configuration → Redirect URLs` (whitelist),
 *     otherwise Supabase will refuse to send the email or will substitute
 *     the Site URL. See `apps/web/.env.example` for the operator note.
 *
 * Visual: same `<AuthShell>` split-screen as `/login` and
 * `/accept-invitation`. Stage-based state machine (`form` / `submitting`
 * / `sent`) mirrors `AcceptInvitationPage`.
 *
 * Flow type: this app uses Supabase JS v2's default PKCE flow (verify
 * in `apps/web/src/auth/supabase.ts` — no explicit `flowType` is
 * passed, so the v2 default applies). The recovery email therefore
 * lands the user on `/reset-password?code=...` (PKCE) rather than the
 * legacy `#access_token=...&type=recovery` hash fragment. See
 * `ResetPasswordPage.tsx` for the `exchangeCodeForSession` consumer.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../auth/supabase';
import { env } from '../../env';
import { AuthShell } from '../../components/auth/AuthShell';

type Stage = 'form' | 'submitting' | 'sent';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<Stage>('form');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email || !email.includes('@')) {
      setError('أدخل بريداً إلكترونياً صحيحاً.');
      return;
    }
    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع ملف apps/web/.env.example.');
      return;
    }
    setStage('submitting');
    try {
      // The redirectTo MUST be whitelisted under Supabase Dashboard →
      // Authentication → URL Configuration → Redirect URLs, otherwise
      // Supabase silently substitutes the Site URL and the user lands
      // on the wrong page.
      const redirectTo = `${window.location.origin}/reset-password`;
      // Intentionally NOT logging or surfacing the error. Per the
      // security posture above, we always show the same "sent" copy.
      await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    } catch {
      // Network / transport failures still funnel into the same UX
      // — never an error banner that leaks the email's status.
    } finally {
      setStage('sent');
    }
  }

  if (stage === 'sent') {
    return (
      <AuthShell>
        <div className="auth-eyebrow">تم الإرسال</div>
        <h1 className="auth-title">
          راجع <em>بريدك</em>
        </h1>
        <p className="auth-sub" style={{ marginBottom: 24 }}>
          إذا كان البريد <strong dir="ltr">{email}</strong> مسجلاً لدينا، أرسلنا لك رابط إعادة تعيين
          كلمة المرور. تحقق من البريد الوارد (وصندوق الـSpam) خلال الدقائق القليلة القادمة. الرابط
          صالح لفترة محدودة.
        </p>
        <Link to="/login" className="auth-cta" style={{ textDecoration: 'none' }}>
          <span>العودة إلى تسجيل الدخول</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      </AuthShell>
    );
  }

  const submitting = stage === 'submitting';

  return (
    <AuthShell>
      <div className="auth-eyebrow">استعادة الوصول</div>
      <h1 className="auth-title">
        نسيت <em>كلمة المرور؟</em>
      </h1>
      <p className="auth-sub">أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.</p>

      <form onSubmit={onSubmit} noValidate>
        {!env.isConfigured ? (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            متغيرات Supabase غير مهيّأة محلياً. أضفها إلى{' '}
            <code style={{ fontFamily: 'var(--font-mono)' }}>apps/web/.env.local</code> ثم أعد
            التشغيل.
          </div>
        ) : null}
        {error ? (
          <div className="banner banner-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="email">
            البريد الإلكتروني
          </label>
          <div className="auth-input-wrap">
            <input
              id="email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="you@super-agency.com"
              disabled={submitting}
              autoComplete="username"
              dir="ltr"
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </div>
        </div>

        <button type="submit" className="auth-cta" disabled={submitting} style={{ marginTop: 8 }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الإرسال…' : 'إرسال رابط إعادة التعيين'}</span>
          {!submitting ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>

        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          <Link to="/login" style={{ color: 'inherit' }}>
            العودة إلى تسجيل الدخول
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
